// src/services/notification.service.js
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { supabase } from '../config/supabase.config';

class NotificationService {
  constructor() {
    this.messaging = null;
    this.currentToken = null;
    this.isInitialized = false;
  }

  // Initialize notification service
  async initialize() {
    try {
      console.log('Starting notification initialization...');
      
      // Check if notifications are supported
      if (!this.isSupported()) {
        console.warn('Notifications are not supported in this browser');
        return { success: false, error: 'Notifications not supported' };
      }

      // Check if Firebase messaging is available
      const { messaging } = await import('../config/firebase.config');
      
      if (!messaging) {
        console.error('Firebase messaging not initialized');
        // Try to initialize messaging directly
        const { initializeApp } = await import('firebase/app');
        const { getMessaging, isSupported } = await import('firebase/messaging');
        
        const firebaseConfig = {
          apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
          authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
          projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
          storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
          appId: process.env.REACT_APP_FIREBASE_APP_ID
        };
        
        const supported = await isSupported();
        if (!supported) {
          return { success: false, error: 'Firebase messaging not supported in this browser' };
        }
        
        const app = initializeApp(firebaseConfig);
        this.messaging = getMessaging(app);
      } else {
        this.messaging = messaging;
      }

      // Register service worker
      const registration = await this.registerServiceWorker();
      if (!registration) {
        return { success: false, error: 'Service worker registration failed' };
      }

      // Request permission
      const permission = await this.requestPermission();
      
      if (permission.success) {
        // Setup message listener for foreground messages
        this.setupMessageListener();
        this.isInitialized = true;
        return { success: true, token: permission.token };
      }
      
      return permission;
    } catch (error) {
      console.error('Error initializing notifications:', error);
      return { success: false, error: error.message };
    }
  }

  // Register service worker
  async registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        // First check if service worker file exists
        const swUrl = '/firebase-messaging-sw.js';
        const response = await fetch(swUrl);
        
        if (!response.ok) {
          console.error('Service worker file not found');
          return null;
        }
        
        const registration = await navigator.serviceWorker.register(swUrl);
        console.log('Service Worker registered:', registration);
        return registration;
      } catch (error) {
        console.error('Service Worker registration failed:', error);
        return null;
      }
    }
    return null;
  }

  // Request notification permission
  async requestPermission() {
    try {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
      
      if (permission === 'granted') {
        // Get FCM token
        const token = await this.getFCMToken();
        
        if (token) {
          return { success: true, token };
        } else {
          return { success: false, error: 'Failed to get FCM token' };
        }
      } else if (permission === 'denied') {
        return { success: false, error: 'Notification permission denied. Please enable it in browser settings.' };
      } else {
        return { success: false, error: 'Notification permission not granted' };
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      return { success: false, error: error.message };
    }
  }

  // Get FCM token
  async getFCMToken() {
    try {
      if (!this.messaging) {
        console.error('Messaging not initialized');
        return null;
      }
      

      const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
      
      if (!vapidKey) {
        console.error('VAPID key not found. Please add REACT_APP_FIREBASE_VAPID_KEY to your .env file');
        return null;
      }

      console.log('Getting FCM token with VAPID key...');
      const currentToken = await getToken(this.messaging, { vapidKey });

      if (currentToken) {
        console.log('FCM Token obtained:', currentToken);
        this.currentToken = currentToken;
        
        // Save token to database
        await this.saveTokenToDatabase(currentToken);
        
        return currentToken;
      } else {
        console.log('No registration token available');
        return null;
      }
    } catch (error) {
      console.error('Error getting FCM token:', error);
      
      // Check for specific errors
      if (error.code === 'messaging/permission-blocked') {
        console.error('Notifications are blocked. Please enable them in browser settings.');
      } else if (error.code === 'messaging/unsupported-browser') {
        console.error('This browser doesn\'t support notifications.');
      } else if (error.code === 'messaging/invalid-vapid-key') {
        console.error('Invalid VAPID key. Please check your Firebase configuration.');
      }
      
      return null;
    }
  }

  // Save FCM token to Supabase
  async saveTokenToDatabase(token) {
    try {
      const userId = localStorage.getItem('userId');
      
      if (!userId) {
        console.warn('No user ID found, cannot save token');
        return;
      }

      // Create table if it doesn't exist (for development)
      // In production, create this table via Supabase dashboard
      const { data: tables } = await supabase
        .from('fcm_tokens')
        .select('id')
        .limit(1);

      if (!tables) {
        console.log('FCM tokens table might not exist, attempting to create...');
        // Table doesn't exist, you'll need to create it in Supabase
        console.warn('Please create fcm_tokens table in Supabase with columns: id, user_id, token, device_info, created_at, updated_at');
        return;
      }

      // Check if token exists
      const { data: existingToken } = await supabase
        .from('fcm_tokens')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (existingToken) {
        // Update existing token
        const { error } = await supabase
          .from('fcm_tokens')
          .update({
            token: token,
            updated_at: new Date().toISOString(),
            device_info: navigator.userAgent
          })
          .eq('user_id', userId);

        if (error) throw error;
        console.log('FCM token updated in database');
      } else {
        // Insert new token
        const { error } = await supabase
          .from('fcm_tokens')
          .insert({
            user_id: userId,
            token: token,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            device_info: navigator.userAgent
          });

        if (error) throw error;
        console.log('FCM token saved to database');
      }
    } catch (error) {
      console.error('Error saving token to database:', error);
      // Don't throw, just log - token is still valid even if not saved to DB
    }
  }

  // Setup message listener for foreground messages
  setupMessageListener() {
    if (!this.messaging) {
      console.error('Messaging not initialized');
      return;
    }

    onMessage(this.messaging, (payload) => {
      console.log('Message received in foreground:', payload);
      
      // Show notification
      this.showNotification(payload);
      
      // Dispatch event
      this.handleNotificationReceived(payload);
    });
  }

  // Show notification
  showNotification(payload) {
    const { title, body, image } = payload.notification || {};
    
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title || 'Payment Reminder', {
        body: body || 'You have a new notification',
        icon: image || '/logo192.png',
        badge: '/logo192.png',
        tag: 'payment-reminder'
      });

      notification.onclick = () => {
        window.focus();
        this.handleNotificationClick(payload);
        notification.close();
      };
    }
  }

  // Handle notification click
  handleNotificationClick(payload) {
    if (payload.data?.type === 'payment_reminder') {
      window.location.href = '#payments';
    }
  }

  // Handle notification received
  handleNotificationReceived(payload) {
    const event = new CustomEvent('notificationReceived', { 
      detail: payload 
    });
    window.dispatchEvent(event);
  }

  // Send test notification
  async sendTestNotification() {
    try {
      this.showNotification({
        notification: {
          title: '🔔 Test Notification',
          body: 'Push notifications are working correctly!',
          icon: '/logo192.png'
        },
        data: {
          type: 'test',
          timestamp: new Date().toISOString()
        }
      });

      return { success: true };
    } catch (error) {
      console.error('Error sending test notification:', error);
      return { success: false, error: error.message };
    }
  }

  // Check if notifications are supported
  isSupported() {
    return 'Notification' in window && 
           'serviceWorker' in navigator && 
           'PushManager' in window;
  }

  // Get permission status
  getPermissionStatus() {
    if (!this.isSupported()) {
      return 'unsupported';
    }
    return Notification.permission;
  }
}

export default new NotificationService();